import React from "react";
import _ from "lodash";
import { useQuery } from "@apollo/client";
import { isEmpty } from "lodash";
import { addDays, isPast, isFuture } from "date-fns";
import pluralize from "pluralize";

import AppBar from "@material-ui/core/AppBar";
import CloseIcon from "@material-ui/icons/Close";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";

import {
  setProperVisitCookiesForRefills,
  setCookiesAndRedirectToPerformanceFollowUpVisit,
  setCookiesAndRedirectToInsomniaFollowUpVisit,
  setProperVisitCookies,
  setCookiesAndRedirectToEditPharmacy,
} from "~/visits/utils/visitUtils";
import {
  clearActiveSubscriptionsBySubType,
  clearUpgradingTo2Pack,
  clearUserAlreadyExists,
  clearVisitCookies,
  setConsultationTypeOrderItem,
  setFollowUpVisitFlag,
  setUpgradingTo2Pack,
  setVisitProduct,
  setActiveSubscriptionBySubType,
} from "~/visits/utils/visitCookies";

import {
  usePortalStyles,
  kickNavy,
  kickRed,
  kickLightBlue,
} from "../styles/portalStyles";

import {
  setPhone,
  setGender,
  setUSState,
} from "~/patient-portal/utils/portalCookies";

import {
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  DB_STATUS_ACTIVE,
  DOCTOR_VISIT_STATUS_AWAITING_REPLY,
  DOCTOR_VISIT_STATUS_CANCELED,
  DOCTOR_VISIT_STATUS_CLOSED_ERROR,
  DOCTOR_VISIT_STATUS_CLOSED_OTHER,
  DOCTOR_VISIT_STATUS_CLOSED_PAYMENT_UNCOLLECTABLE,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_PRIMARY,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_URGENT,
  DOCTOR_VISIT_STATUS_CLOSED_REFUNDED,
  DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
  DOCTOR_VISIT_STATUS_PENDING,
  DOCTOR_VISIT_STATUS_READY,
  FILL_PHARMACY_ID_TRUEPILL,
  ORDER_TYPE_PROP_1_PACK,
  ORDER_TYPE_PROP_2_PACK_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED,
  ORDER_TYPE_PROP_2_PACK_FLAVORED,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP,
  ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED,
  VISIT_TYPE_INSOMNIA_FOLLOW_UP,
  VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
  SUBSCRIPTION_SUB_TYPE_SLEEP,
} from "~/shared/sharedConstants";

import {
  displayFriendlyLongDate,
  displayFriendlyPrescriptionExpirationLongDate,
  displayFriendlyShortenedOrderType,
  formatPhoneNumber,
  getConsultationNameFromVisitType,
  getOrderTypeImageSrc,
  getVisitTypeImageSrc,
  hasRefillsAvailable,
  isDoctorVisitComplete,
  isOriginalOrFollowUpVisit,
  isPharmacyPickupOrderType,
  isVisitTypeInsomnia,
  isConsultationTypeInsomnia,
  latestVisitCompletedAtOrVisitStartedAt,
  numRefillsRemaining,
  displayOrderTypePacks,
} from "~/shared/utils/sharedUtils";

import {
  DOCTOR_VISIT_STATUS_CLOSED_COMPLETE,
  DOCTOR_VISIT_STATUS_PENDING_PHOTOS,
  DOCTOR_VISIT_STATUS_PENDING_PAYMENT,
  VISIT_TYPE_INSOMNIA,
  VISIT_TYPE_PERFORMANCE_ANXIETY,
  COLOR_KICK_RED,
  COLOR_KICK_BLUE,
  COLOR_KICK_YELLOW,
} from "~/shared/sharedConstants";

import { GET_PATIENT_RECORD } from "./portalQueries";

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(1),
  },
}));

function StyledButton({ children, style, ...props }) {
  return (
    <Button
      color="primary"
      size="large"
      style={{
        backgroundColor: COLOR_KICK_BLUE,
        color: "white",
        marginTop: "10px",
        width: "300px",
        ...style,
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

function AffiliateLink({ setShowAffiliateLink }) {
  const { loading, error, data } = useQuery(GET_PATIENT_RECORD);
  if (data && data.patientRecord?.patient?.affiliateToken) {
    return (
      <AppBar
        style={{
          color: "white",
          textAlign: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: "1300",
          padding: "4px 0",
        }}
      >
        <div style={{ alignSelf: "center" }}>
          <div style={{ padding: "0 20px" }}>
            Share this link with your friends to give them a discount off their
            first visit!
          </div>
          <div>
            <a
              style={{ color: kickLightBlue, fontSize: "16px" }}
              target="_blank"
              href={`${window.location.origin}?via=${data.patientRecord.patient.affiliateToken}`}
            >
              gokick.com?via=
              {data.patientRecord.patient.affiliateToken}
            </a>
          </div>
        </div>
        <div
          style={{
            cursor: "pointer",
            position: "absolute",
            right: "20px",
          }}
        >
          <CloseIcon onClick={() => setShowAffiliateLink(false)} />
        </div>
      </AppBar>
    );
  }
  return null;
}

function PersonaVerificationLink({ visit }) {
  return (
    <StyledButton
      onClick={async () => {
        window.location.href =
          visit.personaHostedURL + `&redirect-uri=${window.location.href}`;
      }}
      style={{
        backgroundColor: COLOR_KICK_RED,
        margin: "24px 0 16px",
      }}
    >
      Complete ID Verification&nbsp;
      <NavigateNextIcon />
    </StyledButton>
  );
}

const orderItemsByID = {};

function PatientVisits(props) {
  const { classes } = props;
  const { loading, error, data } = useQuery(GET_PATIENT_RECORD);

  const patient = data?.patientRecord?.patient;
  if (patient) {
    // make sure these cookies are set to enable 1st time visits
    setPhone(patient.phone);
    setGender(patient.gender);
    setUSState(patient.usState);
  }

  if (loading) return "Loading...";
  if (error) return `Error! ${error.message}`;

  // build orderItemsByID map
  data.patientRecord.visits.forEach(({ orderItems }) => {
    orderItems?.forEach((orderItem) => {
      orderItemsByID[orderItem.id] = orderItem;
    });
  });

  return <ShowVisits patientRecord={data.patientRecord} classes={classes} />;
}

const closedUnpaidVisitStatuses = [
  DOCTOR_VISIT_STATUS_CANCELED,
  DOCTOR_VISIT_STATUS_CLOSED_ERROR,
  DOCTOR_VISIT_STATUS_CLOSED_OTHER,
  DOCTOR_VISIT_STATUS_CLOSED_PAYMENT_UNCOLLECTABLE,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_PRIMARY,
  DOCTOR_VISIT_STATUS_CLOSED_REFERRAL_URGENT,
  DOCTOR_VISIT_STATUS_CLOSED_REFUNDED,
];

const pendingVisitStatuses = [
  DOCTOR_VISIT_STATUS_PENDING_PAYMENT,
  DOCTOR_VISIT_STATUS_PENDING_PHOTOS,
  DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
];

function isPast45DaysAndPending(visit) {
  return (
    pendingVisitStatuses.includes(visit.status) &&
    isPast(addDays(new Date(visit.createdAt), 45))
  );
}

function ShowVisits(props) {
  const { patientRecord } = props;
  if (patientRecord == null) return;

  const hasAnyPerformanceAnxietyVisitsToShow = patientRecord.visits.some(
    (v) =>
      v.visitType === VISIT_TYPE_PERFORMANCE_ANXIETY && // only show PA visits
      !closedUnpaidVisitStatuses.includes(v.status) && // not closed unpaid
      !isPast45DaysAndPending(v)
  );

  const hasAnyInsomniaTreatmentsToShow = patientRecord.visits.some(
    (v) =>
      v.visitType === VISIT_TYPE_INSOMNIA &&
      !closedUnpaidVisitStatuses.includes(v.status) &&
      !isPast45DaysAndPending(v)
  );

  clearActiveSubscriptionsBySubType();
  const activeSubsBySubType = {};
  patientRecord.subscriptions.forEach((sub) => {
    if (sub.status === DB_STATUS_ACTIVE) {
      activeSubsBySubType[sub.subType] = sub;
      setActiveSubscriptionBySubType(sub.subType, sub);
    }
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
        Your Treatments and Prescriptions
      </h3>

      {/* show insomnia promo if there are visits, but no insomnia visits */}
      {!hasAnyInsomniaTreatmentsToShow &&
        hasAnyPerformanceAnxietyVisitsToShow && <InsomniaUpsellCard />}

      {!hasAnyPerformanceAnxietyVisitsToShow &&
        hasAnyInsomniaTreatmentsToShow && <StartPAVisitButton />}

      {patientRecord.visits.map((visit) => {
        return (
          <React.Fragment key={visit.id}>
            {renderVisitCard(visit, patientRecord.patient, activeSubsBySubType)}
          </React.Fragment>
        );
      })}

      {!hasAnyPerformanceAnxietyVisitsToShow &&
        !hasAnyInsomniaTreatmentsToShow && <NoVisitsCTA />}
    </div>
  );
}

function PendingVisitCard({ visit }) {
  return (
    <DrawVisitCard
      visit={visit}
      medicationOrVisitName={getConsultationNameFromVisitType(visit.visitType)}
      expiresAtText={displayFriendlyLongDate(
        latestVisitCompletedAtOrVisitStartedAt(
          visit.visitDate,
          visit.patientCompletedAt
        )
      )}
      refillsRemainingTest={""}
      imageSrc={getOrderTypeImageSrc(visit.orderType)}
      cardText={
        <>
          <div>
            Thanks for completing your visit.
            <br />
            We'll send an email as soon as it's reviewed.
          </div>
        </>
      }
    />
  );
}

function AwaitingReplyCard({ visit }) {
  return (
    <DrawVisitCard
      visit={visit}
      medicationOrVisitName={getConsultationNameFromVisitType(visit.visitType)}
      expiresAtText={displayFriendlyLongDate(
        latestVisitCompletedAtOrVisitStartedAt(
          visit.visitDate,
          visit.patientCompletedAt
        )
      )}
      refillsRemainingTest={""}
      imageSrc={getVisitTypeImageSrc(visit.visitType)}
      cardText={
        <>
          <div>
            Thanks for completing your visit.
            <br />
            Please check your messages. We're waiting for your reply before we
            can move forward.
          </div>
        </>
      }
    />
  );
}

function IncompleteVisitCard({ visit, patient }) {
  // return if not pending payment or pending photos
  if (
    visit.status !== DOCTOR_VISIT_STATUS_PENDING_PAYMENT &&
    visit.status !== DOCTOR_VISIT_STATUS_PENDING_PHOTOS
  ) {
    // TODO: show some type of card for the other statuses?
    return null;
  }

  // if visit is older than 45 days, dont show
  const createdAt = new Date(visit.createdAt);
  if (isPast(addDays(createdAt, 45))) {
    return null;
  }

  return (
    <DrawVisitCard
      visit={visit}
      medicationOrVisitName={getConsultationNameFromVisitType(visit.visitType)}
      expiresAtText={displayFriendlyLongDate(
        latestVisitCompletedAtOrVisitStartedAt(
          visit.visitDate,
          visit.patientCompletedAt
        )
      )}
      refillsRemainingTest={""}
      imageSrc={getVisitTypeImageSrc(visit.visitType)}
      buttonText={"Finish Incomplete Visit  >"}
      buttonOnClick={() => {
        switch (visit.status) {
          case DOCTOR_VISIT_STATUS_PENDING_PHOTOS:
            setCookiesAndRedirectToSelectPharmacy(
              patient.id,
              patient.email,
              patient.phone,
              visit
            );
            break;
          case DOCTOR_VISIT_STATUS_PENDING_PAYMENT:
            setCookiesAndRedirectToSelectPharmacy(
              patient.id,
              patient.email,
              patient.phone,
              visit
            );
            break;
        }
      }}
    />
  );
}

function renderVisitCard(visit, patient, activeSubsBySubType) {
  /*
  POSSIBLE STATES
    DoctorVisitStatusPaidReady
    DoctorVisitStatusPending
    DoctorVisitStatusPendingPayment
    DoctorVisitStatusPendingPhotos
    DoctorVisitStatusAwaitingPatientReply
    DoctorVisitStatusClosedComplete
    DoctorVisitStatusRefunded
    DoctorVisitStatusAwaitingPatientReply
    DoctorVisitStatusPendingReviewAdmin
    DoctorVisitStatusPendingVerification
  */

  switch (visit.status) {
    case DOCTOR_VISIT_STATUS_PENDING_PAYMENT:
    case DOCTOR_VISIT_STATUS_PENDING_PHOTOS:
      return <IncompleteVisitCard visit={visit} patient={patient} />;
    case DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN:
    case DOCTOR_VISIT_STATUS_PENDING:
    case DOCTOR_VISIT_STATUS_READY:
      return <PendingVisitCard visit={visit} patient={patient} />;
    case DOCTOR_VISIT_STATUS_AWAITING_REPLY:
      return <AwaitingReplyCard visit={visit} patient={patient} />;
    case DOCTOR_VISIT_STATUS_PENDING_VERIFICATION:
      return <PendingVerificationCard visit={visit} patient={patient} />;
    default:
      return ShowVisitPrescriptionsForVisit(
        visit,
        patient,
        activeSubsBySubType
      );
  }
}

function InsomniaUpsellCard() {
  return (
    <Card
      style={{
        border: "1px solid gray",
        borderRadius: "6px",
        marginBottom: "20px",
        paddingBottom: "20px",
        width: "350px",
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        flexDirection: "column",
        backgroundColor: COLOR_KICK_BLUE,
      }}
    >
      <p
        style={{
          fontSize: "20px",
          fontFamily: "BasisGrotesquePro-Bold",
          color: "white",
          marginTop: "20px",
          marginBottom: "16px",
        }}
      >
        — Now Available —
      </p>
      <p
        style={{
          fontSize: "16px",
          fontFamily: "BasisGrotesquePro-Bold",
          color: "white",
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        Trusted Performance Sleep Treatments from
        <br /> an Expert Stanford Sleep Doctor
      </p>
      <p
        style={{
          fontSize: "16px",
          fontFamily: "BasisGrotesquePro-Regular",
          color: COLOR_KICK_YELLOW,
          marginBottom: "16px",
          textAlign: "center",
        }}
      >
        Go from 'Sleep-Deprived' to 'Feeling Alive' without Bedtime
        Procrastination
      </p>
      <div
        style={{
          width: "88%",
          borderRadius: "2px",
          padding: "12px 10px 6px 10px",
          marginBottom: "24px",
          backgroundColor: "white",
          fontFamily: "BasisGrotesquePro-Regular",
        }}
      >
        <p style={{ fontFamily: "BasisGrotesquePro-Regular" }}>
          <span
            style={{
              fontSize: "16px",
              fontFamily: "BasisGrotesquePro-Bold",
            }}
          >
            Sleep medications include:
          </span>
          <br /> Hydroxyzine, Trazodone, Ramelteon, <br />
          Quetiapine, Cyclobenzaprine, Guanfacine & more
        </p>
        <p style={{ fontFamily: "BasisGrotesquePro-Regular" }}>
          <span
            style={{
              fontSize: "16px",
              fontFamily: "BasisGrotesquePro-Bold",
            }}
          >
            Unlock up to 37% savings on:
          </span>
          <br />
          Fitness Memberships, Yoga Classes, Sleep Equipment & more with your
          HSA or FSA
        </p>
        <p style={{ fontFamily: "BasisGrotesquePro-Bold" }}>
          Starting at only $69
        </p>
      </div>
      <StyledButton
        onClick={async () => {
          clearVisitCookies();
          window.location.href = `/visits/inc/1`;
        }}
        style={{
          backgroundColor: COLOR_KICK_RED,
        }}
      >
        Start Insomnia Visit&nbsp;
        <NavigateNextIcon />
      </StyledButton>
    </Card>
  );
}

function StartPAVisitButton() {
  return (
    <StyledButton
      onClick={async () => {
        clearVisitCookies();
        window.location.href = `/visits/pac/1`;
      }}
      style={{
        width: "352px",
        margin: "20px 0",
      }}
    >
      Start Performance Anxiety Visit&nbsp;
      <NavigateNextIcon />
    </StyledButton>
  );
}

function NoVisitsCTA() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <p style={{ fontSize: "20px", textAlign: "center" }}>
        Treatments and prescriptions will appear here after your first doctor
        visit is reviewed
      </p>

      <StartPAVisitButton />

      <StyledButton
        onClick={() => {
          clearVisitCookies();
          window.location.href = `/visits/inc/1`;
        }}
        style={{
          width: "352px",
        }}
      >
        Start Insomnia Visit&nbsp;
        <NavigateNextIcon />
      </StyledButton>
    </div>
  );
}

function DrawVisitCard(props) {
  const {
    visit,
    medicationOrVisitName,
    expiresAtText,
    refillsRemainingText,
    buttonText,
    imageSrc,
    buttonOnClick,
    cardText,
  } = props;

  let imgHeight = "100px";
  let imgWidth = "214px";
  let imgLeft = "16px";

  if (isVisitTypeInsomnia(visit.visitType)) {
    imgHeight = "190px";
    imgWidth = "160px";
    imgLeft = "0";
  }

  return (
    <Card
      key={visit.id}
      style={{
        border: "1px solid gray",
        borderRadius: "6px",
        marginBottom: "20px",
        paddingBottom: "20px",
        width: "350px",
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        flexDirection: "column",
      }}
    >
      <h3>{medicationOrVisitName}</h3>
      <p>
        {expiresAtText}
        <br />
        {refillsRemainingText}
      </p>
      {visit.orderItems?.length ? (
        visit.orderItems.map((orderItem, idx) => {
          return (
            <>
              <h4
                style={{
                  marginTop: idx == 0 ? "20px" : "40px",
                  padding: "0 10px",
                }}
              >
                {displayOrderTypePacks(
                  orderItem.productSlug,
                  orderItem.subcategory
                )}
              </h4>
              <img
                style={getOrderTypeImageStyle(orderItem.productSlug)}
                src={getOrderTypeImageSrc(
                  orderItem.productSlug,
                  orderItem.subcategory
                )}
              />
            </>
          );
        })
      ) : (
        <img
          style={{
            height: imgHeight,
            width: imgWidth,
            position: "relative",
            left: imgLeft,
            marginTop: "10px",
          }}
          src={imageSrc}
        />
      )}
      <br />
      {buttonText && (
        <StyledButton onClick={buttonOnClick}>{buttonText}</StyledButton>
      )}
      {cardText && (
        <>
          <div
            style={{
              marginTop: "20px",
              padding: "0 20px",
              color: kickNavy,
              fontSize: "16px",
            }}
          >
            {cardText}
          </div>
          {![
            DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
            DOCTOR_VISIT_STATUS_AWAITING_REPLY,
          ].includes(visit.status) && (
            <>
              {isPharmacyPickupOrderType(visit.orderType)
                ? showLocalPharmacy(visit, true)
                : showShippingAddress(visit)}
            </>
          )}
        </>
      )}
    </Card>
  );
}

function ShowVisitPrescriptionsForVisit(visit, patient, activeSubsBySubType) {
  switch (visit.visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      return ShowPerformanceAnxietyPrescription(visit, patient);

    case VISIT_TYPE_INSOMNIA:
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      return ShowInsomniaTrialPrescriptions(
        visit,
        patient,
        activeSubsBySubType
      );
  }

  return ShowPerformanceAnxietyPrescription(visit, patient);
}

function showShippingAddress(visit) {
  return (
    <div
      style={{
        margin: "10px 0 20px",
        fontSize: "15px",
        backgroundColor: kickLightBlue,
        border: "1px solid #ddd",
        padding: "12px",
        borderRadius: "6px",
        width: "280px",
      }}
    >
      <div style={{ color: kickRed, margin: "0 0 6px" }}>
        Your meds will be shipped to:
      </div>
      <div>
        {visit.address1} {visit.address2}
      </div>
      <div>
        {visit.city}, {visit.usState} {visit.zip}
      </div>
    </div>
  );
}

function showLocalPharmacy(visit, pending = false) {
  const { data } = useQuery(GET_PATIENT_RECORD);

  const lockedUntil = new Date(data?.patientRecord?.patient?.lockedUntil);
  const patientIsLocked = isFuture(lockedUntil);

  const pharmacyInfo = visit.pharmacyInfo;
  if (!pharmacyInfo) return null;

  const canEditPharmacy =
    [
      DOCTOR_VISIT_STATUS_AWAITING_REPLY,
      DOCTOR_VISIT_STATUS_PENDING_REVIEW_ADMIN,
      DOCTOR_VISIT_STATUS_PENDING_VERIFICATION,
      DOCTOR_VISIT_STATUS_PENDING,
      DOCTOR_VISIT_STATUS_READY,
    ].includes(visit.status) && !patientIsLocked;

  return (
    <div
      style={{
        margin: "10px 0 20px",
        fontSize: "15px",
        backgroundColor: kickLightBlue,
        border: "1px solid #ddd",
        padding: "12px",
        borderRadius: "6px",
        width: "280px",
      }}
    >
      <div style={{ color: kickRed, margin: "0 0 6px" }}>
        {pending
          ? "Your medication will be available at:"
          : "Pick up your medication at:"}
      </div>
      <div style={{ fontWeight: "bold", fontSize: "18px", margin: "0 0 6px" }}>
        {pharmacyInfo.pharmacyName}
      </div>
      <div>
        {pharmacyInfo.pharmacyAddress1} {pharmacyInfo.pharmacyAddress2}
      </div>
      <div>
        {pharmacyInfo.pharmacyCity}, {pharmacyInfo.pharmacyState}{" "}
        {pharmacyInfo.pharmacyZip}
      </div>
      <div style={{ marginTop: "4px" }}>
        <a
          href={`tel:${pharmacyInfo.pharmacyPhone}`}
          style={{ textDecoration: "none" }}
        >
          {formatPhoneNumber(pharmacyInfo.pharmacyPhone)}
        </a>
      </div>
      {canEditPharmacy && (
        <StyledButton
          onClick={() => {
            setCookiesAndRedirectToEditPharmacy(visit);
          }}
          style={{ width: "160px", fontSize: "14px", marginTop: "16px" }}
        >
          Change Pharmacy
        </StyledButton>
      )}
    </div>
  );
}

function deDupedPrescriptions(visit) {
  let prescriptionsByMedName = {};
  const sortedRxs = _.sortBy(visit.visitPrescriptions, "createdAt").reverse();
  sortedRxs.forEach((rx) => {
    if (!prescriptionsByMedName[rx.medicationName + rx.medicationFormFactor]) {
      prescriptionsByMedName[rx.medicationName + rx.medicationFormFactor] = rx;
    }
  });

  return _.sortBy(Object.values(prescriptionsByMedName), "createdAt").reverse();
}

function ShowInsomniaTrialPrescriptions(visit, patient, activeSubsBySubType) {
  // only show completed visit prescriptions that have a doctorPrescription attached
  if (
    !isDoctorVisitComplete(visit.status) ||
    isEmpty(visit.visitPrescriptions)
  ) {
    return null;
  }

  return (
    <Card
      key={visit.id}
      style={{
        border: "1px solid gray",
        borderRadius: "6px",
        marginBottom: "20px",
        paddingBottom: "20px",
        width: "350px",
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        flexDirection: "column",
      }}
    >
      <h3>{getConsultationNameFromVisitType(visit.visitType)}</h3>
      <h4>
        {visit.visitPrescriptions.map((p) => {
          return (
            <span key={`${visit.id}-${p.id}`}>
              {`${p.medicationName} ${p.medicationDosage}`}
              <br />
            </span>
          );
        })}
      </h4>
      <p>{displayFriendlyLongDate(visit.visitDate)}</p>

      {isPharmacyPickupOrderType(visit.orderType) && showLocalPharmacy(visit)}

      <img
        style={{
          width: "160px",
          height: "190px",
          position: "relative",
          marginTop: "10px",
        }}
        src={getVisitTypeImageSrc(visit.visitType)}
      ></img>
      <br />
      <p>
        Adjust Sleep Meds or Refill Anytime
        <br />
        Included With Membership at No Extra Cost
      </p>
      {/* Closed visits - show refill or follow up button */}
      {visit.status == DOCTOR_VISIT_STATUS_CLOSED_COMPLETE && (
        <StyledButton
          onClick={async () => {
            setCookiesAndRedirectToInsomniaFollowUpVisit(patient, visit);
          }}
        >
          Start Follow-up Visit&nbsp;
          <NavigateNextIcon />
        </StyledButton>
      )}
    </Card>
  );
}

function RenderVisitPrescription({ visit, prescription }) {
  let prescriptionExpired = false;
  let numRefills = numRefillsRemaining(
    prescription.refills,
    prescription.numRefillsFilled
  );

  const orderItem = orderItemsByID[prescription.orderItemID];

  if (!orderItem) return null;

  // only check if there are refills on non-pharmacy pickup (i.e. mail order) orders
  if (!isPharmacyPickupOrderType(orderItem.productSlug)) {
    // check if prescription has expired
    let prescriptionDate = new Date(prescription.createdAt);
    let prescriptionExpires = addDays(prescriptionDate, 362);

    if (isPast(prescriptionExpires) || prescription.refillsCanceled) {
      prescriptionExpired = true;
    }

    // if fill pharmacy is Truepill - set to no refills so we require a follow up visit
    if (
      prescription.fillPharmacyID == FILL_PHARMACY_ID_TRUEPILL ||
      prescription.fillPharmacyID == ""
    ) {
      numRefills = 0;
    }
  }

  return (
    <div
      style={{
        border: "1px solid #ddd",
        borderRadius: "4px",
        margin: "0 -5px 20px",
        padding: "10px",
      }}
    >
      <h4 style={{ maxWidth: "300px", textAlign: "center" }}>
        {prescription.medicationName} {prescription.medicationDosage} - {"  "}
        {displayFriendlyShortenedOrderType(
          orderItem.productSlug,
          orderItem.subcategory
        )}
      </h4>
      <p>
        {prescriptionExpired ? "Expired " : "Expires "}
        {displayFriendlyPrescriptionExpirationLongDate(visit.visitDate)}
        <br />
        {/* don't show number of refills if pharmacy pickup or prescription has expired*/}
        {isPharmacyPickupOrderType(orderItem.productSlug) || prescriptionExpired
          ? ""
          : numRefills == 0
          ? "No refills remaining"
          : `${pluralize("refill", numRefills, true)} remaining`}
      </p>
      {isPharmacyPickupOrderType(orderItem.productSlug)
        ? showLocalPharmacy(visit)
        : showShippingAddress(visit)}
      <img
        style={getOrderTypeImageStyle(orderItem.productSlug)}
        src={getOrderTypeImageSrc(orderItem.productSlug, orderItem.subcategory)}
      />
    </div>
  );
}

function getOrderTypeImageStyle(productSlug) {
  switch (productSlug) {
    case ORDER_TYPE_PROP_1_PACK_FLAVORED:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED:
    case ORDER_TYPE_PROP_1_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_2_PACK_FLAVORED_FOLLOW_UP:
    case ORDER_TYPE_PROP_TRIAL_PACK_FLAVORED:
      return {
        height: "187px",
        position: "relative",
        margin: "-14px 0",
      };
    default: {
      if (productSlug.includes("sleep")) {
        return {
          width: "160px",
          position: "relative",
          marginTop: "10px",
        };
      }
      return {
        height: "100px",
        width: "214px",
        position: "relative",
        left: "16px",
        marginTop: "10px",
      };
    }
  }
}

function shouldShowUpgradeTo2PackButton(visit) {
  const orderItemsInclude1Pack = visit.orderItems?.length
    ? visit.orderItems.some(
        (orderItem) => orderItem.productSlug === ORDER_TYPE_PROP_1_PACK
      )
    : visit.orderType == ORDER_TYPE_PROP_1_PACK;
  return orderItemsInclude1Pack;
}

function renderUpgradeTo2PackButton(patient, prescription) {
  return (
    <>
      <StyledButton
        style={{ marginTop: "16px" }}
        onClick={() => {
          setCookiesAndRedirectToPerformanceFollowUpVisit(
            patient,
            prescription
          );
          setVisitProduct(ORDER_TYPE_PROP_2_PACK_FOLLOW_UP);
          setConsultationTypeOrderItem(
            CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
            ORDER_TYPE_PROP_2_PACK_FOLLOW_UP
          );
          setUpgradingTo2Pack(true);
        }}
      >
        Upgrade to 2 pack
        <NavigateNextIcon />
      </StyledButton>
      <div style={{ marginTop: "4px", padding: "0 10px" }}>
        Complete a follow-up visit to double your prescription amount
      </div>
    </>
  );
}

function ShowPerformanceAnxietyPrescription(visit, patient) {
  // Add state for modal
  const [showPharmacyModal, setShowPharmacyModal] = React.useState(false);

  // only show completed visit prescriptions that have a doctorPrescription attached
  if (
    !isDoctorVisitComplete(visit.status) ||
    !isOriginalOrFollowUpVisit(visit.orderType) ||
    isEmpty(visit.visitPrescriptions)
  ) {
    return null;
  }

  // TODO: Fix this  - only showing first prescription
  // grab the first prescription for now
  var prescription = visit.visitPrescriptions[0];
  // console.log("prescription:", prescription);

  let doRefill = false;
  let prescriptionExpired = false;
  let numRefills = numRefillsRemaining(
    prescription.refills,
    prescription.numRefillsFilled
  );

  // only check if there are refills on non-pharmacy pickup (i.e. mail order) orders
  if (!isPharmacyPickupOrderType(visit.orderType)) {
    doRefill = hasRefillsAvailable(
      prescription.refills,
      prescription.numRefillsFilled
    );

    // check if prescription has expired
    let prescriptionDate = new Date(prescription.createdAt);
    let prescriptionExpires = addDays(prescriptionDate, 362);

    if (isPast(prescriptionExpires) || prescription.refillsCanceled) {
      prescriptionExpired = true;
      doRefill = false;
    }
  }

  return (
    <Card
      key={visit.id}
      style={{
        border: "1px solid gray",
        borderRadius: "6px",
        marginBottom: "20px",
        paddingBottom: "20px",
        width: "350px",
        display: "flex",
        alignItems: "center",
        textAlign: "center",
        flexDirection: "column",
      }}
    >
      <h3>{getConsultationNameFromVisitType(visit.visitType)}</h3>
      {visit.orderItems?.length ? (
        deDupedPrescriptions(visit).map((prescription) => (
          <RenderVisitPrescription
            key={`visitPrescriptions-${prescription.id}`}
            visit={visit}
            prescription={prescription}
          />
        ))
      ) : (
        <>
          <h4>
            {prescription.medicationName} {prescription.medicationDosage} -{" "}
            {"  "}
            {displayFriendlyShortenedOrderType(visit.orderType)}
          </h4>
          <p>
            {prescriptionExpired ? "Expired " : "Expires "}
            {displayFriendlyPrescriptionExpirationLongDate(visit.visitDate)}
            <br />
            {/* don't show number of refills if pharmacy pickup or prescription has expired*/}
            {isPharmacyPickupOrderType(visit.orderType) || prescriptionExpired
              ? ""
              : numRefills == 0
              ? "No refills remaining"
              : `${pluralize("refill", numRefills, true)} remaining`}
          </p>
          {isPharmacyPickupOrderType(visit.orderType) &&
            showLocalPharmacy(visit)}
          <img
            style={{
              height: "100px",
              width: "214px",
              position: "relative",
              left: "16px",
              marginTop: "10px",
            }}
            src={getOrderTypeImageSrc(visit.orderType)}
          ></img>
        </>
      )}
      <br />

      <Dialog
        open={showPharmacyModal}
        onClose={() => setShowPharmacyModal(false)}
        aria-labelledby="pharmacy-alert-dialog-title"
        aria-describedby="pharmacy-alert-dialog-description"
      >
        <DialogTitle id="pharmacy-alert-dialog-title">
          Have you already used the included refill?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="pharmacy-alert-dialog-description">
            Your prescription comes with 1 refill. You can get it by contacting
            your pharmacy directly by calling them or ordering from their
            website. Would you still like to proceed with a new follow-up visit?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPharmacyModal(false)} color="primary">
            Cancel
          </Button>
          <Button
            color="primary"
            size="large"
            style={{
              backgroundColor: COLOR_KICK_BLUE,
              color: "white",
            }}
            onClick={() => {
              setShowPharmacyModal(false);
              setCookiesAndRedirectToPerformanceFollowUpVisit(
                patient,
                prescription
              );
            }}
            autoFocus
          >
            Proceed
            <NavigateNextIcon />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Closed visits - show refill or follow up button */}
      {visit.status == DOCTOR_VISIT_STATUS_CLOSED_COMPLETE && (
        <StyledButton
          onClick={async () => {
            const prescriptionDate = new Date(prescription.createdAt);
            if (
              isPharmacyPickupOrderType(visit.orderType) &&
              isFuture(addDays(prescriptionDate, 30))
            ) {
              setShowPharmacyModal(true);
            } else if (doRefill) {
              setCookiesAndRedirectToPerformanceVisitPaymentRefill(
                patient,
                visit,
                prescription
              );
            } else {
              setCookiesAndRedirectToPerformanceFollowUpVisit(
                patient,
                prescription
              );
            }
          }}
        >
          {doRefill ? "Order a Refill" : "Quick Follow-up Visit"}&nbsp;
          <NavigateNextIcon />
        </StyledButton>
      )}
      {shouldShowUpgradeTo2PackButton(visit) &&
        renderUpgradeTo2PackButton(patient, prescription)}
    </Card>
  );
}

function PendingVerificationCard({ visit }) {
  const createdAt = new Date(visit.createdAt);
  if (isPast(addDays(createdAt, 45))) {
    return null;
  }
  return (
    <DrawVisitCard
      visit={visit}
      medicationOrVisitName={getConsultationNameFromVisitType(visit.visitType)}
      expiresAtText={displayFriendlyLongDate(
        latestVisitCompletedAtOrVisitStartedAt(
          visit.visitDate,
          visit.patientCompletedAt
        )
      )}
      refillsRemainingTest={""}
      imageSrc={getVisitTypeImageSrc(visit.visitType)}
      cardText={
        <>
          <div>
            <strong>Your Visit is Pending ID Verificaion</strong>
            <br />
            <br />
            <span style={{ color: kickRed, fontWeight: "bold" }}>
              ID verification is still required before
              <br />a doctor can review your visit
            </span>
            <br />
            <PersonaVerificationLink visit={visit} />
          </div>
        </>
      }
    />
  );
}

export default function PortalPrescriptionRefills() {
  const classes = useStyles();
  const portalClasses = usePortalStyles();
  const [showAffiliateLink, setShowAffiliateLink] = React.useState(true);
  clearUserAlreadyExists();
  clearUpgradingTo2Pack();

  return (
    <main className={classes.content}>
      {/* TODO: start showing after feature tested in production */}
      {/* {showAffiliateLink && (
        <AffiliateLink setShowAffiliateLink={setShowAffiliateLink} />
      )} */}

      <PatientVisits
        classes={portalClasses}
        style={portalClasses.kickContainer}
      ></PatientVisits>
    </main>
  );
}

function setCookiesAndRedirectToPerformanceVisitPaymentRefill(
  patient,
  visit,
  prescription
) {
  setProperVisitCookiesForRefills(patient, visit, prescription);
  const consultationType =
    CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;

  if (visit.orderItems && visit.orderItems.length > 1) {
    window.location.href = `/visits/${consultationType}/choose-refill-order-items`;
  } else {
    window.location.href = `/visits/${consultationType}/payment/order-details`;
  }
}

function setCookiesAndRedirectToSelectPharmacy(userID, email, phone, visit) {
  setProperVisitCookies(userID, visit.id, "", email, phone);
  setFollowUpVisitFlag(
    [
      VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP,
      VISIT_TYPE_INSOMNIA_FOLLOW_UP,
    ].includes(visit.visitType)
      ? "yes"
      : ""
  );

  let consultationType;

  switch (visit.visitType) {
    case VISIT_TYPE_PERFORMANCE_ANXIETY:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;
      break;
    case VISIT_TYPE_PERFORMANCE_ANXIETY_FOLLOW_UP:
      consultationType =
        CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP;
      break;
    case VISIT_TYPE_INSOMNIA:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_INSOMNIA;
      break;
    case VISIT_TYPE_INSOMNIA_FOLLOW_UP:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP;
      break;
    default:
      consultationType = CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY;
  }

  window.location.href = `/visits/${consultationType}/select-pharmacy`;
}
